import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

interface CurrentManuscript {
  id: string;
  title: string;
  author: string;
  genre: string;
  wordCount: number;
  progress: number;
  deadline: string | null;
  daysRemaining: number | null;
  status: string;
}

interface PendingApplication {
  id: string;
  title: string;
  author: string;
  genre: string;
  wordCount: number;
  appliedDate: string;
  status: string;
}

export const ReaderDashboardPage = () => {
  const [readerName, setReaderName] = useState('Reader');
  const [stats, setStats] = useState({
    currentManuscripts: 0,
    pendingApplications: 0,
    completedReads: 0,
    averageRating: 0,
  });
  const [currentManuscripts, setCurrentManuscripts] = useState<CurrentManuscript[]>([]);
  const [pendingApplications, setPendingApplications] = useState<PendingApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Get reader name from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setReaderName(user.name || user.email?.split('@')[0] || 'Reader');
      } catch (err) {
        console.error('Error parsing user:', err);
      }
    }

    fetchDashboardData();
  }, []);

  const formatGenre = (genre: string) => {
    const genreMap: Record<string, string> = {
      FANTASY: 'Fantasy',
      SCIENCE_FICTION: 'Science Fiction',
      MYSTERY: 'Mystery',
      THRILLER: 'Thriller',
      ROMANCE: 'Romance',
      HISTORICAL_FICTION: 'Historical Fiction',
      LITERARY_FICTION: 'Literary Fiction',
      CONTEMPORARY: 'Contemporary',
      YOUNG_ADULT: 'Young Adult',
      MIDDLE_GRADE: 'Middle Grade',
      HORROR: 'Horror',
      WOMENS_FICTION: 'Women\'s Fiction',
    };
    return genreMap[genre] || genre;
  };

  const formatTimeSince = (date: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.betaApplications.myApplications, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to fetch dashboard data');
        setLoading(false);
        return;
      }

      const applications = data.applications || [];

      // Separate accepted and pending applications
      const accepted = applications.filter((app: any) => app.status === 'ACCEPTED');
      const pending = applications.filter((app: any) => app.status === 'PENDING');

      // Transform accepted applications to current manuscripts
      const currentMSS = accepted.map((app: any) => ({
        id: app.manuscript?.id || app.id,
        title: app.manuscript?.title || 'Unknown Title',
        author: app.manuscript?.author?.displayName || 'Unknown Author',
        genre: formatGenre(app.manuscript?.primaryGenre || ''),
        wordCount: app.manuscript?.wordCount || 0,
        progress: 0, // Progress tracking not available yet
        deadline: app.manuscript?.betaDeadline || null,
        daysRemaining: app.manuscript?.betaDeadline
          ? Math.ceil((new Date(app.manuscript.betaDeadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
          : null,
        status: 'reading',
      }));

      // Transform pending applications
      const pendingApps = pending.map((app: any) => ({
        id: app.id,
        title: app.manuscript?.title || 'Unknown Title',
        author: app.manuscript?.author?.displayName || 'Unknown Author',
        genre: formatGenre(app.manuscript?.primaryGenre || ''),
        wordCount: app.manuscript?.wordCount || 0,
        appliedDate: formatTimeSince(new Date(app.createdAt)),
        status: 'pending',
      }));

      setCurrentManuscripts(currentMSS);
      setPendingApplications(pendingApps);
      setStats({
        currentManuscripts: accepted.length,
        pendingApplications: pending.length,
        completedReads: 0, // Would require additional backend support
        averageRating: 0, // Would require additional backend support
      });

      setLoading(false);
    } catch (err) {
      console.error('Fetch dashboard data error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const recommendedManuscripts: any[] = [
    // Recommendation system would require additional backend support
  ];

  // Generate recent activity from applications data
  const recentActivity = React.useMemo(() => {
    const activities: any[] = [];

    // Add accepted applications as "manuscript assigned" activities
    currentManuscripts.forEach((manuscript: any) => {
      const createdDate = new Date();
      activities.push({
        id: `assigned-${manuscript.id}`,
        type: 'manuscript_assigned',
        message: `You started reading "${manuscript.title}"`,
        date: createdDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: createdDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        timestamp: createdDate.getTime(),
      });
    });

    // Add pending applications as "application submitted" activities
    pendingApplications.forEach((app: any) => {
      const createdDate = new Date();
      activities.push({
        id: `applied-${app.id}`,
        type: 'application_submitted',
        message: `You applied to read "${app.title}"`,
        date: createdDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: createdDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        timestamp: createdDate.getTime(),
      });
    });

    // Sort by timestamp (most recent first) and limit to 5
    return activities
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 5);
  }, [currentManuscripts, pendingApplications]);

  const getProgressColor = (progress: number): string => {
    if (progress >= 80) return theme.colors.success;
    if (progress >= 50) return theme.colors.primary;
    return theme.colors.textTertiary;
  };

  const getStatusBadge = (status: string): React.ReactElement => {
    const statusConfig: Record<string, { variant: 'primary' | 'warning' | 'success' | 'default'; text: string }> = {
      reading: { variant: 'primary', text: 'Reading' },
      pending: { variant: 'warning', text: 'Pending' },
      accepted: { variant: 'success', text: 'Accepted' },
      completed: { variant: 'default', text: 'Completed' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const getActivityIcon = (type: string): string => {
    const icons: Record<string, string> = {
      feedback_submitted: '✍️',
      application_accepted: '✓',
      manuscript_assigned: '📖',
      application_submitted: '📝',
    };
    return icons[type] || '•';
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Welcome Section */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Welcome back, {readerName}
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Here's your reading dashboard
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading your dashboard...
              </p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {!loading && error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchDashboardData}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Stats Cards */}
        {!loading && !error && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '24px',
          marginBottom: '40px',
        }}>
          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '500',
              color: theme.colors.primary,
              marginBottom: '8px',
            }}>
              {stats.currentManuscripts}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Current Manuscripts
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '500',
              color: theme.colors.warning,
              marginBottom: '8px',
            }}>
              {stats.pendingApplications}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Pending Applications
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '500',
              color: theme.colors.success,
              marginBottom: '8px',
            }}>
              {stats.completedReads}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Completed Reads
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '500',
              color: theme.colors.primary,
              marginBottom: '8px',
            }}>
              {stats.averageRating}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Average Rating
            </div>
          </Card>
        </div>
        )}

        {/* Quick Actions */}
        <Card style={{
          backgroundColor: theme.colors.primaryLight,
          marginBottom: '40px',
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Quick Actions
          </h2>
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
          }}>
            <Button onClick={() => window.location.href = '/manuscripts/discover'}>
              Discover Manuscripts
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/reader/applications'}>
              View Applications
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/reader/profile/edit'}>
              Edit Profile
            </Button>
          </div>
        </Card>

        {/* Current Manuscripts */}
        <div style={{ marginBottom: '40px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '400',
              color: theme.colors.textPrimary,
            }}>
              Currently Reading
            </h2>
            <Button
              variant="ghost"
              onClick={() => window.location.href = '/reader/manuscripts'}
            >
              View All
            </Button>
          </div>

          {currentManuscripts.length === 0 ? (
            <Card>
              <p style={{
                textAlign: 'center',
                color: theme.colors.textSecondary,
                fontSize: '16px',
                padding: '40px 20px',
              }}>
                You're not currently reading any manuscripts.{' '}
                <a
                  href="/manuscripts/discover"
                  style={{
                    color: theme.colors.primary,
                    textDecoration: 'none',
                  }}
                >
                  Discover manuscripts
                </a>
              </p>
            </Card>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
              {currentManuscripts.map((manuscript) => (
                <Card key={manuscript.id}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '24px',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px',
                      }}>
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: '500',
                          color: theme.colors.textPrimary,
                          margin: 0,
                        }}>
                          {manuscript.title}
                        </h3>
                        {getStatusBadge(manuscript.status)}
                      </div>
                      <p style={{
                        fontSize: '16px',
                        color: theme.colors.textSecondary,
                        marginBottom: '12px',
                      }}>
                        by {manuscript.author}
                      </p>
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        fontSize: '14px',
                        color: theme.colors.textSecondary,
                        marginBottom: '16px',
                      }}>
                        <span>{manuscript.genre}</span>
                        <span>•</span>
                        <span>{manuscript.wordCount.toLocaleString()} words</span>
                        <span>•</span>
                        <span>Due: {manuscript.deadline}</span>
                        <span>•</span>
                        <span style={{
                          color: manuscript.daysRemaining !== null && manuscript.daysRemaining <= 5
                            ? theme.colors.error
                            : theme.colors.textSecondary,
                        }}>
                          {manuscript.daysRemaining} days remaining
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '8px',
                          fontSize: '14px',
                          color: theme.colors.textSecondary,
                        }}>
                          <span>Reading Progress</span>
                          <span>{manuscript.progress}%</span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: theme.colors.border,
                          borderRadius: theme.borderRadius.small,
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${manuscript.progress}%`,
                            height: '100%',
                            backgroundColor: getProgressColor(manuscript.progress),
                            transition: 'width 0.3s ease',
                          }}></div>
                        </div>
                      </div>

                      <div style={{
                        display: 'flex',
                        gap: '12px',
                      }}>
                        <Button
                          size="small"
                          onClick={() => window.location.href = `/reader/manuscripts/${manuscript.id}`}
                        >
                          Continue Reading
                        </Button>
                        <Button
                          size="small"
                          variant="outline"
                          onClick={() => window.location.href = `/reader/manuscripts/${manuscript.id}/feedback`}
                        >
                          Submit Feedback
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Pending Applications */}
        <div style={{ marginBottom: '40px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '400',
              color: theme.colors.textPrimary,
            }}>
              Pending Applications
            </h2>
          </div>

          {pendingApplications.length === 0 ? (
            <Card>
              <p style={{
                textAlign: 'center',
                color: theme.colors.textSecondary,
                fontSize: '16px',
                padding: '40px 20px',
              }}>
                No pending applications
              </p>
            </Card>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '16px',
            }}>
              {pendingApplications.map((application) => (
                <Card key={application.id}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '500',
                      color: theme.colors.textPrimary,
                      margin: 0,
                      flex: 1,
                    }}>
                      {application.title}
                    </h3>
                    {getStatusBadge(application.status)}
                  </div>
                  <p style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '12px',
                  }}>
                    by {application.author}
                  </p>
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    fontSize: '13px',
                    color: theme.colors.textSecondary,
                    marginBottom: '12px',
                  }}>
                    <span>{application.genre}</span>
                    <span>•</span>
                    <span>{application.wordCount.toLocaleString()} words</span>
                  </div>
                  <p style={{
                    fontSize: '13px',
                    color: theme.colors.textTertiary,
                    marginBottom: '12px',
                  }}>
                    Applied on {application.appliedDate}
                  </p>
                  <Button
                    size="small"
                    variant="outline"
                    onClick={() => window.location.href = `/manuscripts/${application.id}/preview`}
                  >
                    View Details
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Recommended Manuscripts */}
        <div style={{ marginBottom: '40px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '400',
              color: theme.colors.textPrimary,
            }}>
              Recommended for You
            </h2>
            <Button
              variant="ghost"
              onClick={() => window.location.href = '/manuscripts/discover'}
            >
              See All
            </Button>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '16px',
          }}>
            {recommendedManuscripts.map((manuscript) => (
              <Card key={manuscript.id}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px',
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    margin: 0,
                    flex: 1,
                  }}>
                    {manuscript.title}
                  </h3>
                  <Badge variant="success">{manuscript.matchScore}% Match</Badge>
                </div>
                <p style={{
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                  marginBottom: '12px',
                }}>
                  by {manuscript.author}
                </p>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  fontSize: '13px',
                  color: theme.colors.textSecondary,
                  marginBottom: '12px',
                }}>
                  <span>{manuscript.genre}</span>
                  <span>•</span>
                  <span>{manuscript.wordCount.toLocaleString()} words</span>
                </div>
                <p style={{
                  fontSize: '13px',
                  color: theme.colors.primary,
                  marginBottom: '12px',
                  fontStyle: 'italic',
                }}>
                  {manuscript.reason}
                </p>
                <Button
                  size="small"
                  onClick={() => window.location.href = `/manuscripts/${manuscript.id}/preview`}
                >
                  View & Apply
                </Button>
              </Card>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '400',
            marginBottom: '24px',
            color: theme.colors.textPrimary,
          }}>
            Recent Activity
          </h2>

          <Card>
            {recentActivity.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
              }}>
                <p style={{
                  fontSize: '15px',
                  color: theme.colors.textSecondary,
                }}>
                  No recent activity yet. Apply to read manuscripts to get started!
                </p>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}>
                {recentActivity.map((activity, index) => (
                  <div
                    key={activity.id}
                    style={{
                      paddingBottom: index < recentActivity.length - 1 ? '16px' : '0',
                      borderBottom: index < recentActivity.length - 1
                        ? `1px solid ${theme.colors.border}`
                        : 'none',
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                    }}>
                      <span style={{
                        fontSize: '20px',
                        lineHeight: '1',
                        marginTop: '2px',
                      }}>
                        {getActivityIcon(activity.type)}
                      </span>
                      <div style={{ flex: 1 }}>
                        <p style={{
                          fontSize: '15px',
                          color: theme.colors.textPrimary,
                          marginBottom: '4px',
                        }}>
                          {activity.message}
                        </p>
                        <p style={{
                          fontSize: '13px',
                          color: theme.colors.textTertiary,
                        }}>
                          {activity.date} at {activity.time}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};
